import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { ArticleHero } from "../ArticleHero";
import HeroImg from "../../assets/images/newborn-green-stroller-portrait.webp";
import newborn2 from "../../assets/images/newborn-baby-girl-cream-wrap-portrait.webp";
import newbornPortraitImage from "../../assets/images/newborn-sleeping-bear-portrait.webp";
import newbornFamilyImage from "../../assets/images/newborn-pink-studio-portrait.webp";
import newbornDetailsImage from "../../assets/images/newborn-pumpkin-wrap-portrait.webp";
import newbornGalleryImage from "../../assets/images/newborn-autumn-close-up.webp";
import newbornFallBasketImage from "../../assets/images/newborn-fall-pumpkin-basket.webp";
import newbornPlaidImage from "../../assets/images/newborn-plaid-outfit-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { SessionTimeline } from "./components/SessionTimeline";
import type { SessionTimelineStep } from "./components/SessionTimeline";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./WhatHappensDuringNewbornSession.scss";

const articleImages: Record<"hero" | "arrival" | "portraits" | "family" | "details" | "gallery", string | undefined> = {
  hero: HeroImg,
  arrival: newborn2,
  portraits: newbornPortraitImage,
  family: newbornFamilyImage,
  details: newbornDetailsImage,
  gallery: newbornGalleryImage,
};

const sessionTimeline: SessionTimelineStep[] = [
  {
    time: "Arrive and settle in",
    title: "A calm beginning",
    description:
      "There is no need to arrive with a sleeping baby. We take a few minutes to get comfortable, talk through the plan and feed or change your baby if needed.",
  },
  {
    time: "Start simply",
    title: "Wrapped portraits",
    description:
      "A soft wrap often helps a newborn feel secure. It also lets us begin photographing while your baby settles into the rhythm of the session.",
  },
  {
    time: "Follow your baby",
    title: "Solo portraits and details",
    description:
      "When your baby is ready, we move into comfortable individual poses and photograph the tiny details you will want to remember.",
  },
  {
    time: "Bring everyone in",
    title: "Parents and siblings",
    description:
      "Family photographs are relaxed and gently directed. Sibling images are usually planned around the older child’s energy rather than saved for a rigid time.",
  },
  {
    time: "Finish without rushing",
    title: "One last look",
    description:
      "We finish when we have a complete gallery—not when a timer runs out. If your baby needs one more feed or cuddle first, that is part of the process.",
  },
];

const relatedGuides = [
  {
    title: "Best Age for Newborn Photos",
    href: "/resources/newborn/best-age-for-newborn-photos/",
    image: newbornPlaidImage,
    imageAlt: "Newborn baby wearing a plaid outfit during an autumn portrait",
  },
  {
    title: "How to Prepare for Your Newborn Session",
    href: "/resources/newborn/how-to-prepare-for-your-newborn-session/",
    image: newbornFallBasketImage,
    imageAlt: "Newborn baby posed in a basket surrounded by autumn pumpkins",
  },
];

export const WhatHappensDuringNewbornSession = (): ReactElement => (
  <main className="newborn-page newborn-article-page newborn-session-guide">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Newborn", to: "/resources/newborn/" },
          { label: "What Happens During a Newborn Session" },
        ]}
        category="Newborn guide"
        description="A relaxed, step-by-step look at the session—from settling in and feeding to family portraits and your finished gallery."
        imageAlt="Sleeping newborn baby during a calm photography session"
        imagePosition="center 42%"
        imageSrc={articleImages.hero ?? HeroImg}
        readingTime="8 min read"
        title="What Happens During a Newborn Session"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout newborn-article-callout--answer">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>What to expect</h2>
            <p>
              Newborn sessions are intentionally unhurried. We begin by settling in and feeding your baby if needed,
              then move through wrapped portraits, simple solo images and photographs with parents and siblings.
              Feeding, changing and soothing breaks are expected. We follow your baby rather than a strict schedule.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#before-session">Before the session begins</a>
            </li>
            <li>
              <a href="#session-flow">The session, step by step</a>
            </li>
            <li>
              <a href="#baby-awake">If your baby is awake or unsettled</a>
            </li>
            <li>
              <a href="#family-photos">Parents and sibling photographs</a>
            </li>
            <li>
              <a href="#session-length">How long the session takes</a>
            </li>
            <li>
              <a href="#after-session">What happens afterward</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section newborn-article-split" id="before-session">
          <div>
            <p className="newborn-article-kicker">A RELAXED ARRIVAL</p>
            <h2>Before the session begins</h2>
            <p>
              You do not need to keep your baby awake, time the final feed perfectly or arrive with everything already
              settled. Newborns rarely follow a plan that closely, and the session is designed with that in mind.
            </p>
            <p>
              We begin with a quick conversation about how your baby is doing, which photographs matter most to you and
              whether anyone needs a little time before we start. If your baby is hungry, we feed first. If a diaper
              needs changing five minutes later, we pause again. Nothing has gone wrong—that is simply a newborn
              session.
            </p>
          </div>
          <ArticlePhoto
            alt="Sleeping newborn baby girl wrapped in cream knit fabric"
            className="newborn-article-split__photo"
            label="PHOTO: SETTLING IN"
            src={articleImages.arrival}
          />
        </section>

        <section className="newborn-article-section newborn-session-flow" id="session-flow">
          <header>
            <p className="newborn-article-kicker">A BABY-LED PROCESS</p>
            <h2>The session, step by step</h2>
            <p>
              This is the usual rhythm, but not a checklist your baby has to complete. I may change the order whenever
              feeding, sleep or an older sibling makes another approach more comfortable.
            </p>
          </header>
          <SessionTimeline steps={sessionTimeline} />
        </section>

        <section className="newborn-session-photo-pair" aria-label="Newborn portrait examples">
          <ArticlePhoto
            alt="Wrapped newborn portrait"
            className="newborn-session-photo-pair__large"
            label="PHOTO: WRAPPED PORTRAIT"
            src={articleImages.portraits}
          />
          <ArticlePhoto
            alt="Close-up photograph of a newborn baby's hands and feet"
            className="newborn-session-photo-pair__small"
            label="PHOTO: LITTLE DETAILS"
            src={articleImages.details}
          />
        </section>

        <section className="newborn-article-section" id="baby-awake">
          <p className="newborn-article-kicker">AWAKE IS NOT A PROBLEM</p>
          <h2>What if my baby does not sleep?</h2>
          <p>
            A sleeping baby can make certain curled portraits easier, but sleep is not the measure of a successful
            session. Quiet, awake photographs often show beautiful eye contact and expressions that parents love.
          </p>
          <p>
            If your baby needs help settling, we slow down. Feeding, swaddling, rocking, cuddling and a clean diaper can
            all be part of the session. I never force a pose or continue when your baby is uncomfortable. Sometimes the
            best choice is to switch from a posed image to a wrapped portrait or a photograph in a parent’s arms.
          </p>

          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>You do not have to keep the session “on track”</h3>
              <p>Your only job is to care for your baby. I will adjust the photography around what they need.</p>
            </div>
          </aside>
        </section>

        <section
          className="newborn-article-section newborn-article-split newborn-article-split--reverse"
          id="family-photos"
        >
          <ArticlePhoto
            alt="Parents holding their newborn baby during a family portrait"
            className="newborn-article-split__photo"
            label="PHOTO: FAMILY PORTRAIT"
            src={articleImages.family}
          />
          <div>
            <p className="newborn-article-kicker">YOUR FAMILY IN THE FRAME</p>
            <h2>Parents and sibling photographs</h2>
            <p>
              You do not need to know how to pose. I will guide you into simple positions, watch the light and make
              small adjustments so the photographs feel connected rather than stiff. These images may include the whole
              family, each parent with the baby and close, quiet moments that focus on connection.
            </p>
            <p>
              With young siblings, we keep expectations realistic. Their part may happen near the beginning, while they
              are curious and rested, or later if they need time to warm up. A short, happy moment is more useful than
              asking a toddler to cooperate for a long stretch.
            </p>
          </div>
        </section>

        <section className="newborn-article-section newborn-session-duration" id="session-length">
          <div>
            <p className="newborn-article-kicker">ROOM FOR BREAKS</p>
            <h2>How long does a newborn session take?</h2>
            <p>
              Newborn sessions can take up to five hours, but that does not mean we photograph continuously for five
              hours. Much of that time may be spent feeding, settling, changing wraps, helping a sibling take a break or
              simply waiting for your baby to become comfortable.
            </p>
            <p>
              Some babies move through the session quickly; others need a slower pace. I leave enough time so that a
              normal newborn moment does not turn into pressure for you or your baby.
            </p>
          </div>
          <dl>
            <div>
              <dt>Up to 5 hours</dt>
              <dd>with feeding and soothing breaks</dd>
            </div>
            <div>
              <dt>Baby-led</dt>
              <dd>no rigid posing schedule</dd>
            </div>
            <div>
              <dt>Flexible order</dt>
              <dd>planned around your family</dd>
            </div>
          </dl>
        </section>

        <section className="newborn-article-section newborn-article-split" id="after-session">
          <div>
            <p className="newborn-article-kicker">AFTER PHOTO DAY</p>
            <h2>What happens after the session?</h2>
            <p>
              After the session, I select the strongest photographs and carefully edit each final image. The goal is a
              cohesive gallery that includes the portraits you hoped for as well as the small moments you may not have
              noticed while they were happening.
            </p>
            <p>
              Your finished photographs are delivered in an online gallery, where you can view your images and choose
              favorites for prints and other artwork. You will receive clear instructions when the gallery is ready, so
              there is no need to make decisions on session day.
            </p>
          </div>
          <ArticlePhoto
            alt="Finished newborn photographs displayed in an online gallery"
            className="newborn-article-split__photo"
            label="PHOTO: FINISHED GALLERY"
            src={articleImages.gallery}
          />
        </section>

        <section className="newborn-article-cta">
          <div>
            <p className="newborn-article-kicker">A CALM EXPERIENCE FROM START TO FINISH</p>
            <h2>Ready to plan your newborn session?</h2>
            <p>
              Review the newborn session styles, finished images and pricing. If you are already booked or need help
              choosing, send me a question.
            </p>
          </div>
          <GuideCtaActions category="newborn" />
        </section>

        <section className="newborn-related-guides">
          <header>
            <h2>Related newborn guides</h2>
            <Link to="/resources/newborn/">
              View all guides <UiIcon name="arrow" />
            </Link>
          </header>
          <div>
            {relatedGuides.map((guide, index) => (
              <Link className="newborn-related-guide" key={guide.href} to={guide.href}>
                <ArticlePhoto
                  alt={guide.imageAlt}
                  className={`newborn-related-guide__photo newborn-related-guide__photo--${index + 1}`}
                  label="PHOTO"
                  src={guide.image}
                />
                <span>{guide.title}</span>
                <UiIcon name="arrow" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  </main>
);

export default WhatHappensDuringNewbornSession;
